import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Firestore path: audit_log/{logId}
 *
 * Append-only trail for security-sensitive actions: cross-tenant admin
 * operations, role changes, document deletions, crisis-query detections,
 * tenant-isolation refusals.
 *
 * Everything sensitive logs here. The BigQuery ai_usage_events table captures
 * LLM-call telemetry; audit_log captures user/admin actions that change state
 * or affect access.
 */
export const AuditAction = z.enum([
    'user.invited',
    'user.role_changed',
    'user.deactivated',
    'user.reactivated',
    'document.uploaded',
    'document.indexed',
    'document.deactivated',
    'document.deleted',
    'category.created',
    'category.merged',
    'category.deleted',
    'provider.created',
    'provider.updated',
    'provider.deactivated',
    'provider.deleted',
    'provider.suggested',
    'provider.suggestion_approved',
    'provider.suggestion_rejected',
    'provider.intake_status_changed',
    'change_request.approved',
    'change_request.rejected',
    'change_request.info_requested',
    'tenant.created',
    'tenant.suspended',
    // Wisselknop SUPPOHRT <-> RHOGO; context { van, naar }.
    'tenant.switched',
    'ring2_source.added',
    'ring2_source.removed',
    'crisis.detected',
    'access.refused',
    'login.success',
    'login.failure',
    'kennisagent.query',
    'kennisagent.error',
    'ingest.lock_cleared_stale_acquiring',
    'ingest.lock_cleared_stale_running',
    'ingest.janitor_error',
    'ingest.upload_started',
    'ingest.upload_finalized',
    'ingest.reingest_started',
    'ingest.delete_executed',
    'ingest.job_failed',
    'ingest.kept_as_download_only',
    'ingest.tier_moved',
    'document.metadata_edited',
    'document.downloaded',
    'provider.enriched_from_website',
    'sara.task_approved',
    'sara.task_deleted',
    'sara.task_purged',
    'sara.audio_deleted',
    'intervisie_group.created',
    'intervisie_group.updated',
    'intervisie_group.deleted',
    /** Pending lid op e-mail werd bij eerste login een echt lidmaatschap. */
    'intervisie_group.member_linked',
    'intervisie_group.member_removed',
    'agenda_event.created',
    'agenda_event.updated',
    'agenda_event.deleted',
    'timechimp.entry_written',
    'timechimp.entry_updated',
    'timechimp.entry_deleted',
    'timechimp.hours_submitted',
    'timechimp.week_written',
    'timechimp.week_duplicated',
    'timechimp.link_changed',
    // Kostendeclaraties (plan: docs/plans/declaraties.md). PII-arm: nooit het
    // rekeningnummer of de omschrijvingen in de context.
    'declaratie.submitted',
    'declaratie.reviewed',
    'declaratie.sent',
    'security_incident.mail_sent',
    'kanaal.message_deleted_by_admin',
    'dm.conversation_deleted_by_admin',
    'user.teamleider_changed',
    'user.ai_tools_changed',
    'user.ai_tools_requested',
    'user.weergavenaam_changed',
    'security_incident.reported',
    'security_incident.triaged',
    'security_incident.reported_to_ap',
    'security_incident.log_linked',
    'security_incident.exported',
    'security_incident.deadline_reminded',
    'security_incident.signal_detected',
    'security_incident.signal_dismissed',
    'elearning_course.created',
    'elearning_course.updated',
    'elearning_course.deleted',
    'elearning.retakes_reset',
    'elearning_webinar.created',
    'elearning_webinar.deleted',
    // Vrijgeven van een "Wat is nieuw"-regel is een broadcast naar elke actieve
    // gebruiker in de tenant; een auditor wil zien wie dat deed en wanneer.
    'changelog.published',
    // Persoonlijke demo-toegangscodes (besluit 8 okt 2026). Nooit de code zelf
    // of de hash in de context.
    'demo_access.created',
    'demo_access.revoked',
    // Verwijsbrief (ADR 0017): aanmaken en kopiëren, nooit de inhoud zelf.
    'verwijsbrief.created',
    'verwijsbrief.copied',
    'verwijsbrief.deleted',
]);
export const AuditLogEntry = z.object({
    id: DocId,
    tenantId: TenantId,
    actorUserId: DocId.optional(),
    actorRole: z.string().max(50).optional(),
    action: AuditAction,
    /**
     * Target resource identifier in path form (e.g. `users/abc`, `documents/xyz`).
     */
    targetPath: z.string().max(500).optional(),
    /**
     * Sanitised context. MUST NOT contain patient data or raw clinical queries.
     * Query text may be reduced to a SHA-256 hash for de-duplication.
     */
    context: z.record(z.unknown()).optional(),
    at: IsoTimestamp,
});
/**
 * `document.downloaded` dekt twee handelingen: een document openen in het
 * voorbeeld (`context.preview`) en het echt downloaden. Het bulk-alarm en de
 * schermen houden die uit elkaar; een voorbeeld telde eerst als download,
 * waardoor 74 bekeken werkbladen als 110 downloads op de kaart stonden.
 */
export function isVoorbeeld(e) {
    return e.context?.preview === true;
}
/**
 * Query params for the suppohrt_admin audit-log inzagepagina
 * (`GET /api/v1/beheer/audit-log`). Newest-first, cursor-paginated.
 *
 * `action` is a PREFIX filter (e.g. `user.` matches all user.* actions).
 * `from`/`to` accept a date (`YYYY-MM-DD`) or a full ISO timestamp; an
 * unparseable value is rejected (400). `cursor` is the opaque doc id of
 * the last row on the previous page.
 */
const ParseableDate = z
    .string()
    .min(1)
    .max(40)
    .refine((s) => !Number.isNaN(Date.parse(s)), { message: 'Ongeldige datum.' });
export const AuditLogQuery = z.object({
    userId: DocId.optional(),
    action: z.string().min(1).max(60).optional(),
    from: ParseableDate.optional(),
    to: ParseableDate.optional(),
    cursor: DocId.optional(),
    limit: z.coerce.number().int().min(1).max(50).optional(),
});
//# sourceMappingURL=audit-log.js.map